import React, {
  useContext,
  DragEvent,
  ChangeEvent,
  useState,
  useEffect,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { useAuthContext } from "../../../AuthContextWrapper";
import {
  OUTCOMES,
} from "../../../constants";
import { Box, Button, Typography } from "@mui/material";
import simplePurpleHouse1 from "../../assets/simple-purple-house-1x.png";
import simplePurpleHouse15 from "../../assets/simple-purple-house-1.5x.png";
import simplePurpleHouse2 from "../../assets/simple-purple-house-2x.png";
import simplePurpleHouse3 from "../../assets/simple-purple-house-3x.png";
import { ScreenFlowFormButtons } from "../shared/ScreenFlowFormButtons";
import { AssetContext } from "../../../AssetContextWrapper";
import { callGet, CancelToken, estateApi } from "../../../apis";
import { AssetImage } from "../../../models/AssetImage";
import { Loading } from "../../shared/Loading";
import { RenderFileUploadBox } from "views/fileCabinet/FileUploadArea";
import { Asset } from "models/Asset";

const NUM_RETRIES = 10;
const RETRY_DELAY = 1000;
const HOME_IMAGE_DIMENSIONS = "150x150";

export const HomePhotoScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow, activeEstate, user } = useAuthContext()
  const { assetsLoaded } = useContext(AssetContext);
  const { screenName } = useParams();
  const cancelTokenSource = CancelToken.source();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);
  const [primaryRealEstate, setPrimaryRealEstate] = useState<Asset | undefined>();
  const inputRef = React.useRef(null);
  const [errors, setErrors] = useState<string[]>();
  const [dragActive, setDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assetImagePath, setAssetImagePath] = useState<string>();
  const [processingImage, setProcessingImage] = useState(false);
  const [uploadedFiles, setUploadedFiles] = React.useState<FileList | undefined>();
  const [previewImageKey, setPreviewImageKey] = React.useState<string>("initialKey");

  useEffect(() => {
    if(!activeEstate?.id) {
      return
    }

    if (activeEstate?.primaryRealEstate) {
      setPrimaryRealEstate(activeEstate?.primaryRealEstate);
    } else {
      const realEstateCategory = 5 
      setPrimaryRealEstate(new Asset(undefined, "Primary Home", "Primary Home", 0, [], activeEstate?.id, realEstateCategory, undefined, undefined, undefined, undefined, undefined));
    }
  }, [activeEstate]);

  useEffect(() => {
    (async () => {
      if (assetsLoaded && primaryRealEstate) {
        if (primaryRealEstate?.images?.length) {
          // get this assets newest image
          const [firstImage] = primaryRealEstate?.images.sort(
            (a: AssetImage, b: AssetImage) =>
              (b?.updatedAt?.getTime() || 0) - (a?.updatedAt?.getTime() || 0)
          );
          // console.log(firstImage?.fileName);
          if (firstImage && firstImage?.fileName) {
            setHomeImage(firstImage.fileName, 1, true);
          }
        }
      }
    })();
  }, [assetsLoaded]);

  const setHomeImage = async (
    fileName: string,
    tryNumber = 1,
    screenInit = false
  ): Promise<void> => {
    if (tryNumber < NUM_RETRIES) {
      // https://app.weareatticus.com/api/v1/asset/image/pwL65YV4VFwOWrYVmcHg9j1IuAoFOUUnv0Nt5nZJqaVWuelD/238x238
      const homeImagePath = `asset/image/${fileName}/${HOME_IMAGE_DIMENSIONS}`;
      try {
        const response = await callGet(homeImagePath, true, {
          responseType: "blob",
          cancelToken: cancelTokenSource.token,
        });
        if (response?.data) {
          const file = new Blob([response.data], {
            type: response?.headers["content-type"] || "image/jpg",
          });
          const urlCreator = window.URL || window.webkitURL;
          const imageUrl = urlCreator.createObjectURL(file);
          setAssetImagePath(imageUrl);
          setProcessingImage(false);
        } else {
          // fail silently
        }
      } catch (error) {
        // possibly not created yet, keep trying, unless this is the initial screen load
        if ((error as any)?.response?.status === 404 && !screenInit) {
          setProcessingImage(true);
          setTimeout(() => {
            (async () => {
              await setHomeImage(fileName, tryNumber + 1);
            })();
          }, RETRY_DELAY);
        }
      }
    } else {
      setProcessingImage(false);
      setAssetImagePath(undefined);
    }
  };

  // handle drag events
  const handleDrag = function (e: DragEvent<HTMLFormElement | HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // triggers when file is dropped
  const handleDrop = function (e: DragEvent<HTMLDivElement>) {
    setErrors(undefined);
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      // at least one file has been dropped so do something
      validateAndUpload(e.dataTransfer.files);
    }
  };

  // triggers when file is selected with click
  const handleChange = function (e: ChangeEvent<HTMLInputElement>) {
    setErrors(undefined);
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      // at least one file has been selected so do something
      // console.log(e.target.files);
      validateAndUpload(e.target.files);
    }
  };

  const validateAndUpload = async (files: FileList): Promise<void> => {
    if (files.length === 0) {
      setErrors(["Please drop or select a file."]);
      return;
    }
    // allow multiple?
    const errs: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      if (!file) {
        errs.push("no file");
      }
    }
    if (errs?.length) {
      setErrors(errs);
    } else {
      if (!(await handleUpload(files))) {
        setErrors(["Sorry, upload failed.  Please try again."]);
      }
    }
  };

  // triggers the input when the button is clicked
  const handleChooseClick = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    inputRef?.current?.click();
  };

  const handleUpload = async (files: FileList): Promise<boolean> => {
    if(!activeEstate?.id || !user?.id) {
      return false;
    }

    setIsSubmitting(true);
    const file = files.item(0);
    if (file) {
      let assetId = primaryRealEstate?.id;
      if(!assetId) {
        console.log("asset not found, saving")
        assetId = await estateApi.saveAsset(primaryRealEstate, activeEstate.id, user.id);
        console.log("asset saved " + assetId)
      }
      
      const uploadResult = await estateApi.saveAssetImage(
        file,
        activeEstate.id,
        user.id,
        assetId
      );
      if (uploadResult?.file_name) {
        setIsSubmitting(false);
        setHomeImage(uploadResult.file_name);
        return true;
      }
    }
    setIsSubmitting(false);
    setErrors(["Sorry, file upload failed.  Please try again."]);
    return false;
  };

  const handleComplete = async (): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  const getHomeElement = (): JSX.Element => {
    if (processingImage || isSubmitting) {
      return <Loading />;
    } else if (assetImagePath) {
      return (
        <img
          src={assetImagePath}
          alt="Primary Home"
          title="Primary Home"
          width="150"
          height="150"
          style={{
            borderRadius: 8,
          }}
        />
      );
    } else {
      return (
        <img
          srcSet={`${simplePurpleHouse15} 1.5x, ${simplePurpleHouse2} 2x, ${simplePurpleHouse3} 3x`}
          src={simplePurpleHouse1}
          alt="Primary Home"
          title="Primary Home"
          width="150"
          height="150"
        />
      );
    }
  };

  return currentScreen ? (
    <FlowScreenWrapper title="Please upload a picture of the home. This will make it easy for you to find it quickly in your Atticus Digital Inventory of Assets.">
      <>
      <div style={{zIndex: 20}}>
          <RenderFileUploadBox
            uploadedFiles={uploadedFiles}
            setUploadedFiles={validateAndUpload}
            setErrors={setErrors}
          
            previewImage={<>
              {getHomeElement()}
              <Typography
                  variant="h4"
                  color="secondary"
                  sx={{
                    pb: 2,
                    fontWeight: 600,
                  }}
                >
                Drag or upload a file here
              </Typography>
            </>}
            key={previewImageKey}
          />
        </div>
       {errors?.length &&
        errors.map((error: string) => (
          <Typography color="error" key={error} sx={{ m: 1 }}>
            {error}
          </Typography>
        ))}
        
        <form onSubmit={handleComplete}>
          <ScreenFlowFormButtons
            isSubmitting={isSubmitting}
            onBack={() => navigate(-1)}
          />
        </form>
      </>
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
